/**
 * Admin Agents API client — FE-22.
 * Calls GET /admin/agents, GET /admin/agents/reports, GET /admin/agents/:id,
 * POST /admin/agents/:id/restrict, POST /admin/agents/:id/restore.
 */

import { apiFetch } from "./client";

// ─── Types ───

export type GovernanceStatus = "active" | "restricted" | "suspended" | "banned";

export type ListingReviewStatus =
  | "submitted"
  | "auto_check_failed"
  | "human_review"
  | "approved"
  | "rejected"
  | "withdrawn";

export type AgentReportType =
  | "credential_phishing"
  | "system_impersonation"
  | "spam"
  | "unauthorized_collection"
  | "other";

export type AgentSummary = {
  readonly id: string;
  readonly name: string;
  readonly creator_id: string;
  readonly creator_name: string;
  readonly connection_mode: string;
  readonly governance_status: GovernanceStatus;
  readonly risk_score: number;
  readonly report_count: number;
  readonly created_at: string;
};

export type AuditEntry = {
  readonly id: string;
  readonly action: string;
  readonly actor_id: string;
  readonly reason: string;
  readonly created_at: string;
};

export type AgentReport = {
  readonly id: string;
  readonly reporter_id: string;
  readonly reason: string;
  readonly status: string;
  readonly created_at: string;
};

export type AdminAgentReport = {
  readonly id: string;
  readonly reporter_id: string;
  readonly reporter_name: string;
  readonly agent_template_id: string;
  readonly agent_name: string;
  readonly report_type: AgentReportType;
  readonly message_id: string | null;
  readonly description: string | null;
  readonly created_at: string;
};

export type H2aStats = {
  readonly active: number;
  readonly suspended: number;
  readonly terminated: number;
};

export type AgentListingState = {
  readonly visibility: string;
  readonly listing_status: string;
  readonly monetization_status: string;
  readonly updated_at: string | null;
};

export type AgentDetail = {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly creator_id: string;
  readonly creator_name: string;
  readonly connection_mode: string;
  readonly governance_status: GovernanceStatus;
  readonly risk_score: number;
  readonly report_count: number;
  readonly created_at: string;
  readonly updated_at: string;
  readonly audit_records: ReadonlyArray<AuditEntry>;
  readonly reports: ReadonlyArray<AgentReport>;
  readonly h2a_stats: H2aStats;
  readonly listing: AgentListingState;
};

export type AgentListingReviewSummary = {
  readonly review_id: string;
  readonly agent_id: string;
  readonly agent_name: string;
  readonly creator_id: string;
  readonly creator_name: string;
  readonly version: number;
  readonly review_status: ListingReviewStatus;
  readonly listing_status: string;
  readonly monetization_status: string;
  readonly manifest_hash: string;
  readonly required_review_reasons: ReadonlyArray<string>;
  readonly submitted_at: string;
  readonly updated_at: string;
};

// ─── Params ───

type ListAgentsParams = {
  readonly governance_status?: GovernanceStatus;
  readonly q?: string;
  readonly limit?: number;
  readonly offset?: number;
};

type ListAgentsResponse = {
  readonly items: AgentSummary[];
  readonly total: number;
};

type ListListingReviewsParams = {
  readonly status?: ListingReviewStatus;
  readonly limit?: number;
  readonly offset?: number;
};

type ListListingReviewsResponse = {
  readonly items: AgentListingReviewSummary[];
  readonly total: number;
};

type ListAgentReportsParams = {
  readonly agent_id?: string;
  readonly report_type?: AgentReportType;
  readonly limit?: number;
  readonly offset?: number;
};

type ListAgentReportsResponse = {
  readonly items: AdminAgentReport[];
  readonly total: number;
};

// ─── API calls ───

export async function fetchAgents(
  params: ListAgentsParams,
): Promise<ListAgentsResponse> {
  const sp = new URLSearchParams();
  if (params.q) sp.set("q", params.q);
  if (params.governance_status)
    sp.set("governance_status", params.governance_status);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.offset) sp.set("offset", String(params.offset));

  const qs = sp.toString();
  const res = await apiFetch<{ data: ListAgentsResponse }>(
    qs ? `/admin/agents?${qs}` : "/admin/agents",
  );
  return res.data;
}

export async function fetchAgentDetail(id: string): Promise<AgentDetail> {
  const res = await apiFetch<{ data: AgentDetail }>(`/admin/agents/${id}`);
  return res.data;
}

export async function fetchAgentReports(
  params: ListAgentReportsParams,
): Promise<ListAgentReportsResponse> {
  const sp = new URLSearchParams();
  if (params.agent_id) sp.set("agent_id", params.agent_id);
  if (params.report_type) sp.set("report_type", params.report_type);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.offset) sp.set("offset", String(params.offset));

  const qs = sp.toString();
  const res = await apiFetch<{ data: ListAgentReportsResponse }>(
    qs ? `/admin/agents/reports?${qs}` : "/admin/agents/reports",
  );
  return res.data;
}

export async function fetchAgentListingReviews(
  params: ListListingReviewsParams,
): Promise<ListListingReviewsResponse> {
  const sp = new URLSearchParams();
  if (params.status) sp.set("status", params.status);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.offset) sp.set("offset", String(params.offset));

  const qs = sp.toString();
  const res = await apiFetch<{ data: ListListingReviewsResponse }>(
    qs
      ? `/admin/agents/listing/reviews?${qs}`
      : "/admin/agents/listing/reviews",
  );
  return res.data;
}

export async function approveAgentListingReview(
  agentId: string,
  version: number,
  reviewerNotes?: string,
): Promise<void> {
  await apiFetch<{ data: unknown }>(
    `/admin/agents/${agentId}/listing/reviews/${version}/approve`,
    {
      method: "POST",
      body: JSON.stringify({ reviewer_notes: reviewerNotes }),
    },
  );
}

export async function rejectAgentListingReview(
  agentId: string,
  version: number,
  reviewerNotes: string,
): Promise<void> {
  await apiFetch<{ data: unknown }>(
    `/admin/agents/${agentId}/listing/reviews/${version}/reject`,
    {
      method: "POST",
      body: JSON.stringify({ reviewer_notes: reviewerNotes }),
    },
  );
}

export async function restrictAgent(id: string, reason: string): Promise<void> {
  await apiFetch<{ data: { restricted: boolean } }>(
    `/admin/agents/${id}/restrict`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

export async function restoreAgent(id: string, reason: string): Promise<void> {
  await apiFetch<{ data: { restored: boolean } }>(
    `/admin/agents/${id}/restore`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

export async function banAgent(id: string, reason: string): Promise<void> {
  await apiFetch<{ data: { banned: boolean } }>(`/admin/agents/${id}/ban`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function unpublishAgentListing(
  id: string,
  reason: string,
): Promise<void> {
  await apiFetch<{ data: AgentListingState }>(
    `/admin/agents/${id}/listing/unpublish`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}
